import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Phone, Mail, MapPin, ChevronRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const ContactHero = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.fromTo(
        '.contact-hero-line',
        { scaleX: 0, opacity: 0 },
        { scaleX: 1, opacity: 1, duration: 1 }
      );

      tl.fromTo(
        '.contact-hero-breadcrumb',
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 },
        '-=0.6'
      );

      tl.fromTo(
        '.contact-hero-title',
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8 },
        '-=0.4'
      );

      tl.fromTo(
        '.contact-hero-subtitle',
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.7 },
        '-=0.4'
      );
    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1423666639041-f56000c27a9a?auto=format&fit=crop&w=2000&q=80"
          alt="Contact Securelec for electrical and security services"
          className="w-full h-full object-cover object-center"
        />
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-black/30" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(229,33,51,0.2)_1px,transparent_1px),linear-gradient(90deg,rgba(229,33,51,0.2)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 pt-24 relative z-10">
        <div className="max-w-2xl">
          {/* Breadcrumbs */}
          <nav className="contact-hero-breadcrumb flex items-center gap-2 text-white/60 text-sm mb-6">
            <a href="/" className="hover:text-white transition-colors">Home</a>
            <ChevronRight className="w-4 h-4" />
            <span className="text-securelec-red">Contact</span>
          </nav>

          <div
            className="contact-hero-line w-24 h-1 bg-gradient-to-r from-securelec-red to-securelec-red/50 mb-6 origin-left rounded-full"
            style={{ boxShadow: '0 0 20px rgba(229, 33, 51, 0.6)' }}
          />
          <h1 className="contact-hero-title text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight drop-shadow-lg">
            Contact Securelec
          </h1>
          <p className="contact-hero-subtitle text-lg md:text-xl text-white/80 leading-relaxed drop-shadow-md">
            Get in touch with our team for electrical or security enquiries across Carrum and the Bayside region.
          </p>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-securelec-light to-transparent" />
    </section>
  );
};

const ContactSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        '.contact-form-block',
        { x: -60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );

      gsap.fromTo(
        '.contact-info-block',
        { x: 60, opacity: 0 },
        {
          x: 0,
          opacity: 1,
          duration: 0.8,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
            toggleActions: 'play none none reverse',
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jotfor.ms/s/umd/latest/for-form-embed-handler.js';
    script.async = true;
    script.onload = () => {
      if (window.jotformEmbedHandler) {
        window.jotformEmbedHandler("iframe[id='JotFormIFrame-260751537454460']", "https://form.jotform.com/");
      }
    };
    document.body.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-24 bg-securelec-light relative overflow-hidden">
      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-64 bg-gradient-to-b from-transparent via-securelec-red to-transparent" />

      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Form - Jotform Embed */}
          <div className="contact-form-block">
            <div data-testid="jotform-container">
              <iframe
                id="JotFormIFrame-260751537454460"
                title="Securelec"
                allowTransparency="true"
                allow="geolocation; microphone; camera; fullscreen; payment"
                src="https://form.jotform.com/260751537454460"
                frameBorder="0"
                style={{ minWidth: '100%', maxWidth: '100%', height: '539px', border: 'none' }}
                scrolling="no"
              />
            </div>
          </div>

          {/* Contact Information */}
          <div className="contact-info-block">
            <span className="inline-flex items-center gap-2 text-securelec-red text-sm font-semibold tracking-widest uppercase mb-4">
              Contact Information
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-securelec-dark mb-6">
              Reach Out Directly
            </h2>
            <div className="w-16 h-1 bg-securelec-red mb-8" />

            <p className="text-lg text-securelec-gray/80 mb-10 leading-relaxed">
              Prefer to speak with us directly? Give us a call or send an email. We're always happy to discuss your electrical or security needs.
            </p>

            <div className="space-y-6">
              {/* Phone Numbers */}
              <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-securelec-red/30 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-securelec-red/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-securelec-red" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-securelec-dark mb-3">Phone</h3>
                    <div className="space-y-2">
                      <a 
                        href="tel:+61413104012" 
                        className="flex items-center gap-2 text-securelec-gray/70 hover:text-securelec-red transition-colors text-base"
                      >
                        <span className="text-securelec-gray/50 text-sm w-12">Mick:</span>
                        <span className="font-medium">+61 413 104 012</span>
                      </a>
                      <a 
                        href="tel:+61423746855" 
                        className="flex items-center gap-2 text-securelec-gray/70 hover:text-securelec-red transition-colors text-base"
                      >
                        <span className="text-securelec-gray/50 text-sm w-12">Tom:</span>
                        <span className="font-medium">+61 423 746 855</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-securelec-red/30 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-securelec-red/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-securelec-red" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-securelec-dark mb-3">Email</h3>
                    <a 
                      href="mailto:hello@securelec.net.au" 
                      className="text-securelec-gray/70 hover:text-securelec-red transition-colors text-base font-medium"
                    >
                      hello@securelec.net.au
                    </a>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="bg-white rounded-2xl p-6 border border-gray-200 hover:border-securelec-red/30 hover:shadow-lg transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-securelec-red/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-securelec-red" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-securelec-dark mb-3">Location</h3>
                    <p className="text-securelec-gray/70 text-base leading-relaxed">
                      PO Box 12006 Carrum Vic 3197
                    </p>
                    <p className="text-securelec-gray/50 text-sm mt-2">
                      Servicing the Bayside and South East Melbourne region.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Licenses */}
            <div className="mt-8 p-6 bg-securelec-dark/5 rounded-2xl">
              <h4 className="text-sm font-semibold text-securelec-dark mb-3 uppercase tracking-wide">Licensed & Certified</h4>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-securelec-gray/50 text-sm">Electrical:</span>
                  <span className="text-securelec-red font-semibold">REC 30321</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-securelec-gray/50 text-sm">Security:</span>
                  <span className="text-securelec-red font-semibold">853-441-92S</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { ContactHero, ContactSection };
